import {
  useProjectId,
  useCreatePackageMutation,
  useStartPipelineMutation,
  useBlobUpload,
  useServiceName,
  ExtendedFile,
} from '@consigli/hooks';
import {
  CreatePackageRequest,
  Language,
  PipelineName,
  DocumentPackageType,
  StartPipelineRequest,
  StartPipelineResponse,
  JobType,
  ProcessorPreferenceKey,
  EmployerRole,
} from '@consigli/types';
import limit from 'p-limit';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

import { Route } from '@/routes';

export interface PackageCreationHook {
  setCurrentCounter: Dispatch<SetStateAction<number>>;
  setTargetCounter: Dispatch<SetStateAction<number>>;
}

export const useCreateInsightPackage = ({
  setCurrentCounter,
  setTargetCounter,
}: PackageCreationHook) => {
  const projectId = useProjectId();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const serviceName = useServiceName();
  const [createPackage] = useCreatePackageMutation();
  const [startPipeline] = useStartPipelineMutation();
  const { upload } = useBlobUpload();

  const createDocumentPackage = useCallback(
    async (
      type: DocumentPackageType,
      packageName: string,
      language: Language,
      needClassification?: boolean,
      selectedConflictTypes?: ProcessorPreferenceKey[],
      employerRole?: EmployerRole | null,
      data?: Record<string, unknown>,
    ) => {
      try {
        const payload: CreatePackageRequest = {
          projectId: projectId,
          type: type,
          name: packageName,
          language,
          needClassification,
          selectedConflictTypes,
          employerRole,
          data,
        };
        const response = await createPackage(payload).unwrap();
        if (response) return response.id;
      } catch (error) {
        console.log('Error creating insight package:', error);
        toast.error(t('package-creation.package-create-failed'));
      }
    },
    [createPackage, projectId, t],
  );

  const uploadWithCounter = useCallback(
    async (file: ExtendedFile, documentPackageId: string) => {
      let blob;
      try {
        blob = await upload(file, documentPackageId);
      } finally {
        setCurrentCounter((counter: number) => counter + 1);
        return blob;
      }
    },
    [upload, setCurrentCounter],
  );

  const uploadFiles = useCallback(
    async (files: ExtendedFile[], documentPackageId: string) => {
      setCurrentCounter(0);
      setTargetCounter(files.length);

      const queue = limit(100);
      const input = files.map((file) => queue(uploadWithCounter, file, documentPackageId));

      await Promise.allSettled(input);
    },
    [setCurrentCounter, setTargetCounter, uploadWithCounter],
  );

  const comparePackages = useCallback(
    async (
      primaryPackageId: string,
      secondaryPackageId: string,
      pipelineName?: PipelineName,
      metadata?: Record<string, unknown>,
    ): Promise<StartPipelineResponse> => {
      const payload: StartPipelineRequest = {
        packageId: primaryPackageId,
        projectId: projectId,
        pipelineName: pipelineName ?? PipelineName.DEFAULT,
        secondaryPackageId: secondaryPackageId,
        notify: true,
        jobType: JobType.DOCUMENT_PACKAGE,
        metadata,
      };
      const response = await startPipeline(payload).unwrap();
      return response;
    },
    [projectId, startPipeline],
  );

  const startAnalysis = useCallback(
    async (packageId: string, pipelineName: PipelineName, metadata?: Record<string, unknown>) => {
      await startPipeline({
        projectId,
        packageId,
        pipelineName,
        notify: true,
        jobType: JobType.DOCUMENT_PACKAGE,
        metadata,
      });

      navigate(
        `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${packageId}/${Route.DASHBOARD}`,
      );
    },
    [startPipeline, projectId, navigate, serviceName],
  );

  return {
    createDocumentPackage,
    upload,
    uploadFiles,
    comparePackages,
    startAnalysis,
  };
};
